import type { SDK_CronosEvm, SDK_L1, SDK_L2 } from '@cronos-app/sdk';
import { ChainLayer, NATIVE_TOKEN_ADDRESS } from '@cronos-app/sdk';
import { currentWallet } from '@cronos-app/wallet';
import type { QueryKey, UseQueryResult } from '@tanstack/react-query';
import { useQueries, useQuery } from '@tanstack/react-query';
import { RefetchInterval } from '@ui/configs/ui';
import { getSDKs } from '@ui/utils';
import { Fraction } from 'bi-fraction';

interface UseTokenBalanceOptions {
  tokenAddress: string;
  account?: string;
  chainLayer: ChainLayer;
  enabled?: boolean;
}

export const getTokenBalanceQueryKey = (
  options: Partial<
    Pick<UseTokenBalanceOptions, 'tokenAddress' | 'account' | 'chainLayer'>
  >,
): QueryKey => {
  const queryKey = [
    'getTokenBalanceQuery',
    options.chainLayer,
    options.tokenAddress,
    options.account,
  ].filter((v) => Boolean(v));
  return queryKey;
};

/**
 * Returns the ERC20 token balance of the given account on the given layer.
 *
 * @param options the token address, account (default is current wallet user), and chain layer
 * @returns the ERC20 token balance of the given account on the given layer.
 */
export const useTokenBalance = (
  options: UseTokenBalanceOptions,
): UseQueryResult<Fraction> => {
  return _useTokenBalance(options);
};

/**
 * Returns the ERC20 token balance of the given account on the L2 layer for the token
 * corresponding to the given L1 token address.
 *
 * @param options the token address on L1, account (default is current wallet user), and chain layer
 * @returns the ERC20 token balance of the given account on the L2 layer for the token
 * corresponding to the given L1 token address
 */
export const useDepositTokenL2Balance = (
  options: Omit<UseTokenBalanceOptions, 'chainLayer'>,
): UseQueryResult<Fraction> => {
  const desiredNetwork = currentWallet.useDesiredNetwork();
  const sdk = getSDKs(desiredNetwork);
  const bridge = sdk[ChainLayer.L2].getBridge(
    sdk[ChainLayer.L1].providerOptions,
  );
  const tokenMappingFn = () =>
    bridge.getDepositTokenL2Address(options.tokenAddress);
  return _useTokenBalance(
    { ...options, chainLayer: ChainLayer.L2 },
    tokenMappingFn,
  );
};

const _useTokenBalance = (
  options: UseTokenBalanceOptions,
  tokenMappingFn: (tokenAddress: string) => Promise<string> = async (
    tokenAddress,
  ) => tokenAddress,
): UseQueryResult<Fraction> => {
  const { tokenAddress, chainLayer } = options;
  const desiredNetwork = currentWallet.useDesiredNetwork();
  const sdk = getSDKs(desiredNetwork)[chainLayer];
  const account = options.account ?? currentWallet.useAccount() ?? '';

  const queryKey = getTokenBalanceQueryKey({
    tokenAddress,
    account: account,
    chainLayer,
  });

  return useQuery({
    queryKey,
    queryFn: getTokenBalanceQueryFn(
      { ...options, sdk, account },
      tokenMappingFn,
    ),
    staleTime: RefetchInterval.Slow,
    refetchInterval: RefetchInterval.Slow,
    enabled: options.enabled === false ? false : !!account,
  });
};

export const useMultiTokenBalances = (
  options: Omit<UseTokenBalanceOptions, 'tokenAddress'> & {
    tokenAddresses: string[];
  },
  tokenMappingFn: (tokenAddress: string) => Promise<string> = async (
    tokenAddress,
  ) => tokenAddress,
) => {
  const { tokenAddresses, chainLayer } = options;
  const desiredNetwork = currentWallet.useDesiredNetwork();
  const sdk = getSDKs(desiredNetwork)[chainLayer];
  const account = options.account ?? currentWallet.useAccount() ?? '';

  return useQueries({
    queries: tokenAddresses.map((tokenAddress) => ({
      queryKey: getTokenBalanceQueryKey({
        tokenAddress,
        account: account,
        chainLayer,
      }),
      queryFn: getTokenBalanceQueryFn(
        { ...options, sdk, account, tokenAddress },
        tokenMappingFn,
      ),
      refetchInterval: RefetchInterval.Slow,
      enabled: options.enabled === false ? false : !!account,
    })),
  });
};

const getTokenBalanceQueryFn = (
  options: Pick<UseTokenBalanceOptions, 'tokenAddress'> & {
    sdk: SDK_L1 | SDK_L2 | SDK_CronosEvm;
    account: string;
  },
  tokenMappingFn: (tokenAddress: string) => Promise<string>,
) => {
  const { sdk, account } = options;
  return async () => {
    const mappedTokenAddress = await tokenMappingFn(options.tokenAddress);
    if (mappedTokenAddress === NATIVE_TOKEN_ADDRESS) {
      return await sdk.getNativeTokenBalance(account);
    }
    const erc20Contract = sdk.getERC20(mappedTokenAddress);
    let balance = BigInt(0);
    let decimals = BigInt(18);
    try {
      [balance, decimals] = await Promise.all([
        erc20Contract.balanceOf(account),
        erc20Contract.decimals(),
      ]);
    } catch (e) {
      // can't read SC -> assume invalid token
    }
    return new Fraction(balance).shr(Number(decimals));
  };
};
